import { useCallback, useRef } from 'react';

import { uploadImportedSvg, uploadMedia } from '../api';

/**
 * Media staged in an editor but not uploaded yet.
 *
 * Picking a file inserts a `blob:` URL into the markdown immediately, so the
 * author sees the picture straight away, and the real upload happens on save.
 * That flow was copied byte-for-byte into three editors (MainWindow,
 * CreateArticle, CreateNews) and is the single largest piece of duplication
 * between them; this is that code, once.
 *
 * The map lives in a ref, not state: replacing it must not re-render the
 * editor mid-typing, and its contents are only ever read at save time.
 */
function usePendingMedia() {
  const pendingRef = useRef(new Map());

  /**
   * Registers a file and returns the blob URL to put in the markdown.
   *
   * `source` says where the file came from. 'pdf-import' is the one case that
   * may carry an SVG: our own importer draws a PDF's vector graphics as one,
   * and those go to a route of their own (see below). A file the author picked
   * has no source and takes the ordinary upload, which refuses SVG.
   */
  const stage = useCallback((file, mediaType, source = null) => {
    const blobUrl = URL.createObjectURL(file);

    pendingRef.current.set(blobUrl, {
      file,
      type: mediaType,
      mimeType: file.type,
      source,
    });

    return blobUrl;
  }, []);

  /**
   * Uploads everything the text still references and rewrites those blob URLs
   * to the stored ones. Entries the author deleted from the text again are
   * revoked without ever being uploaded — which is the point of staging.
   * `target` is 'article' or 'news': it decides which right the upload needs.
   *
   * Nothing is forgotten until every upload went through: a failed upload
   * leaves all files staged for the next attempt. The caller puts the returned
   * text back into the draft before saving, so a save that fails afterwards
   * (a title already taken) keeps the stored URLs, not dead blob ones.
   */
  const uploadPending = useCallback(async (markdownText, target) => {
    const uploaded = [];

    for (const [blobUrl, media] of Array.from(
      pendingRef.current.entries(),
    )) {
      if (!markdownText.includes(blobUrl)) continue;

      const isImportedSvg =
        media.source === 'pdf-import' &&
        media.mimeType === 'image/svg+xml';

      const data = isImportedSvg
        ? await uploadImportedSvg(media.file, target)
        : await uploadMedia(media.file, target);

      uploaded.push([blobUrl, data.url]);
    }

    let finalText = markdownText;

    for (const [blobUrl, url] of uploaded) {
      finalText = finalText.replaceAll(blobUrl, url);
    }

    for (const blobUrl of Array.from(pendingRef.current.keys())) {
      if (!markdownText.includes(blobUrl) || uploaded.some(([done]) => done === blobUrl)) {
        URL.revokeObjectURL(blobUrl);
        pendingRef.current.delete(blobUrl);
      }
    }

    return finalText;
  }, []);

  /** Abandoning the editor: nothing was uploaded, so only the URLs leak. */
  const discardPending = useCallback(() => {
    for (const blobUrl of pendingRef.current.keys()) {
      URL.revokeObjectURL(blobUrl);
    }

    pendingRef.current.clear();
  }, []);

  return { stage, uploadPending, discardPending };
}

export default usePendingMedia;
